import {
  createComputed,
  createContext,
  createMemo,
  For,
  getOwner,
  JSX,
  Owner,
  Show,
  useContext,
} from "solid-js";
import { useParams } from "@solidjs/router";
import c from "class-c";

import ScrollView from "@repo/ui/containers/ScrollView";
import Line from "@repo/ui/decoration/Line";
import ListTile from "@repo/ui/info/ListTile";
import assert from "@repo/utils/assert";
import Money from "@repo/utils/Money";
import Table from "@repo/utils/Table";
import UArray from "@repo/utils/UArray";
import UDate from "@repo/utils/UDate";
import USolid from "@repo/utils-solid/USolid";

import { Bookable, useBookables } from "~/state/features/bookables";
import { Membership, useMemberships } from "~/state/features/memberships";
import { Organization, useOrganizations } from "~/state/features/organizations";
import { Product, useProducts } from "~/state/features/products";
import { Promotion, usePromotions } from "~/state/features/promotions";

import styles from "./Cart.module.scss";

type Cart = Cart.Entry[];

declare namespace Cart {
  type Props = {
    class?: string;
    children: JSX.Element;
  };

  type Entry = {
    itemId: Item.Id;
    cost?: Entry.Cost;
    quantity: number;
    label?: string;
    promoCodeId?: Promotion.Code.Id;
    metadata?: Record<string, any>;
  };
  namespace Entry {
    type Cost = Partial<Record<Cart.Entry.Cost.Unit, number>>;
    namespace Cost {
      type Unit = (typeof Cart.Entry.Cost.units)[number];
    }
  }

  type Item = Bookable.Resolved | Membership.Plan | Product;

  namespace Item {
    type Id = Item["id"];
  }

  type Total = Record<Cart.Entry.Cost.Unit, number>;
}

function Cart({ class: className, children: _children }: D<Cart.Props>) {
  const owner = getOwner()!;

  const children = USolid.children(() => (
    // Use Cart.Entry as data when passed as child of Cart
    // See Cart.Entry implementation
    <CartContext.Provider value={{ entriesAsData: true }}>
      {_children}
    </CartContext.Provider>
  ));

  if (import.meta.env.DEV)
    createComputed(() => {
      if (
        !children.toArray().every((child: any) => child?.type === "Cart.Entry")
      )
        throw new Error("Cart may not have children other than Cart.Entry.");
    });

  const entriesProps = createMemo(() =>
    (
      children
        .toArray()
        .filter(
          (child: any) => child?.type === "Cart.Entry",
        ) as unknown as Cart.Entry.Data[]
    ).map(({ props }) => props),
  );
  const entries = createMemo(() => entriesProps().map(({ entry }) => entry));

  return (
    <div class={c(styles)`cart`.c`column ${className}`}>
      <ScrollView class={c`${styles["entries"]} expand column`}>
        <For each={entriesProps()}>{(props) => <Entry {...props} />}</For>

        <div class="expand" />
        <ListTile
          class="m-0"
          propsFor={{ title: { class: "color:text-weak" } }}
          title="Subtotal"
          trailing={Entry.Cost.render(Cart.subtotal(entries(), owner))}
        />
        <Show when={!!Cart.discount(entries(), owner).cents}>
          <ListTile
            class="m-0 pt-1"
            propsFor={{ title: { class: "color:text-weak" } }}
            title="Discounts"
            trailing={Entry.Cost.render(Cart.discount(entries(), owner))}
          />
        </Show>
        <Show when={!!Cart.tax(entries(), owner).cents}>
          <ListTile
            class="m-0 pt-1"
            propsFor={{ title: { class: "color:text-weak" } }}
            title="Tax"
            trailing={Entry.Cost.render(Cart.tax(entries(), owner))}
          />
        </Show>
        <div class="mb-3" />
      </ScrollView>
      <Line class="mb-3" />
      <ListTile
        class="text:heading-4 text:++ m-0"
        title="Total"
        trailing={Entry.Cost.render(Cart.total(entries(), owner))}
      />
    </div>
  );
}

Cart.subtotal = (cart: Cart, owner: Owner) =>
  Table.sumColumns(
    cart.map(
      (entry): Cart.Entry.Cost =>
        entry.cost || {
          cents:
            Cart.Entry.resolveItem(entry, owner)?.priceCents! * entry.quantity,
        },
    ),
    Cost.units,
  );

Cart.discount = (cart: Cart, owner: Owner): Cart.Entry.Cost =>
  USolid.runWithOwnerTracked(owner, () => ({
    cents: -UArray.sum(cart, (entry) => Entry.discountCents(entry, owner)),
  }));

Cart.tax = (cart: Cart, owner: Owner): Cart.Entry.Cost =>
  USolid.runWithOwnerTracked(owner, () => {
    const params = useParams<{ orgId: Organization.Id }>();
    const [organizations] = useOrganizations();
    const taxSettings = organizations.byId.taxSettings(params.orgId);

    if (!taxSettings) return {};

    return Table.sumColumns(
      cart.map((entry): Cart.Entry.Cost => {
        const taxRate = (() => {
          if (Bookable.Id.is(entry.itemId))
            return taxSettings.bookingTaxRate || 0;
          if (Membership.Plan.Id.is(entry.itemId))
            return taxSettings.membershipTaxRate || 0;
          return taxSettings.productTaxRate || 0;
        })();

        const price = () =>
          entry.cost?.cents ??
          (Cart.Entry.resolveItem(entry, owner)?.priceCents ?? 0) *
            entry.quantity;

        return {
          cents:
            (price() - Entry.discountCents(entry, owner)) * (taxRate / 100),
        };
      }),
      Cost.units,
    );
  });

Cart.total = (cart: Cart, owner: Owner): Cart.Total =>
  Table.sumColumns(
    [
      Cart.subtotal(cart, owner),
      Cart.tax(cart, owner),
      Cart.discount(cart, owner),
    ],
    Cost.units,
  );

Cart.applyPromoCode = (
  cart: Cart,
  promoCode: Promotion.Code | undefined,
  owner: Owner,
) => {
  if (!promoCode) return cart;
  if (cart.length === 0) return cart;

  const res = [...cart];

  const index = UArray.maxIndexBy(res, (entry) => {
    if (entry.promoCodeId === promoCode.id) return Infinity;
    if (
      Cart.Item.Id.codeApplies(entry.itemId, promoCode) &&
      (entry.cost?.credits || 0) < entry.quantity &&
      (entry.cost?.guestPasses || 0) < entry.quantity &&
      !entry.promoCodeId
    )
      return entry.cost?.cents
        ? entry.cost?.cents / entry.quantity
        : Entry.resolveItem(entry, owner)?.priceCents ?? 0;
  });
  if (index == null) return res;

  const entry = res[index]!;
  // If the entry has more than one quantity, we need to split discounted
  // entries from standard entries to unambiguously display promo application.
  if (entry.quantity > 1) {
    const unitPrice = Entry.resolveItem(entry, owner)?.priceCents ?? 0;
    entry.quantity -= 1;
    if (entry.cost?.cents) entry.cost.cents -= unitPrice;
    res.splice(index, 0, {
      ...entry,
      cost: {
        cents: unitPrice,
      },
      quantity: 1,
      promoCodeId: promoCode.id,
    });
  } else {
    entry.promoCodeId = promoCode.id;
  }

  return res;
};

declare namespace Cart.Entry {
  type Props = {
    entry: Cart.Entry;
    action?: JSX.Element;
    children?: JSX.Element;
  };

  type Data = {
    type: "Cart.Entry";
    props: Props;
  };
}

Cart.Entry = Entry;
function Entry(props: D<Cart.Entry.Props>) {
  const { entry, action, children } = props satisfies D;
  const context = useContext(CartContext);
  if (context?.entriesAsData)
    return { type: "Cart.Entry", props } satisfies Cart.Entry.Data as never;

  const owner = getOwner()!;

  const [bookables] = useBookables();
  const [promotions] = usePromotions();

  const item = createMemo(() => Entry.resolveItem(entry, owner));
  const promoCode = () => promotions.codes.byId(entry.promoCodeId)!;

  return (
    <>
      <ListTile
        propsFor={{
          subtitle: {
            class: "text:body-regular color:text-weak",
          },
        }}
        label={entry.label}
        title={
          <>
            {item()?.name}
            {entry.quantity > 1 && (
              <span class="text:body-regular color:text-moderate-weak">
                {" "}
                ×{entry.quantity}
              </span>
            )}
          </>
        }
        subtitle={(() => {
          const { itemId } = entry;
          if (Bookable.Id.is(itemId)) {
            const bookable = () => bookables.byId(itemId);
            return `${UDate.format(bookable()?.startAt, "Mmm d, yyyy")}, ${UDate.format.time(bookable()?.startAt, "h:mm apm")}`;
          }
        })()}
        trailing={
          <div>
            <div class="row gap-1">
              {children || <div class="expand" />}
              <div>
                {(() => {
                  const cents = item()?.priceCents! * entry.quantity;

                  const moneyString = Money.format(cents);
                  if (entry.cost == null) return moneyString;

                  const costString = Entry.Cost.format(entry.cost);
                  if (moneyString === costString) return moneyString;

                  return (
                    <>
                      <s class="color:text-moderate-weak">{moneyString}</s>{" "}
                      <span class="inline-block">{costString}</span>
                    </>
                  );
                })()}
              </div>
            </div>
            <div>{action}</div>
          </div>
        }
      />
      <Show when={Entry.discountCents(entry, owner)}>
        {(discount) => (
          <ListTile
            class="-mt-1 text:body-regular color:primary"
            propsFor={{
              title: { class: "ml-2" },
            }}
            title={`${promoCode().code} – ${Promotion.format(promoCode())} off`}
            trailing={Money.format(-discount())}
          />
        )}
      </Show>
    </>
  );
}

Entry.resolveItem = (entry: Cart.Entry, owner: Owner) =>
  Cart.Item.Id.resolve(entry.itemId, owner);

Entry.discountCents = (entry: Cart.Entry, owner: Owner) =>
  USolid.runWithOwnerTracked(owner, () => {
    if (!entry.promoCodeId) return 0;
    const [promotions] = usePromotions();
    const promoCode = promotions.codes.byId(entry.promoCodeId)!;

    assert(
      Cart.Item.Id.codeApplies(entry.itemId, promoCode),
      "Invalid promo code.",
    );

    const price = () =>
      entry.cost?.cents ??
      (Cart.Entry.resolveItem(entry, owner)?.priceCents ?? 0) * entry.quantity;

    return promoCode.unit === "percent"
      ? price() * (promoCode.value / 100)
      : Math.min(promoCode.value, price());
  });

namespace Item {
  export type Id = Cart.Item["id"];
  export namespace Id {
    export const resolve = <T extends Cart.Item.Id | undefined>(
      id: T,
      owner: Owner,
    ) =>
      USolid.runWithOwnerTracked(owner, () => {
        if (!id) return;

        const [bookables] = useBookables();
        const [memberships] = useMemberships();
        const [products] = useProducts();

        if (Bookable.Id.is(id)) return bookables.byId(id)!;
        if (Membership.Plan.Id.is(id)) return memberships.planById(id)!;
        return products.byId(id)!;
      });

    export function codeApplies(id?: Cart.Item.Id, code?: Promotion.Code) {
      if (!id || !code) return false;
      if (code.itemTypes.booking && Bookable.Id.is(id)) return true;
      if (code.itemTypes.membership && Membership.Plan.Id.is(id)) return true;
      if (code.itemTypes.product && Product.Id.is(id)) return true;
      return false;
    }
  }
  export function max(item: Cart.Item | undefined) {
    if (!item) return Infinity;
    if ("stock" in item && typeof item.stock === "number") return item.stock;
    if ("availability" in item && typeof item.availability === "number")
      return item.availability;
    return Infinity;
  }
}
Cart.Item = Item;

namespace Cost {
  export const units = ["cents", "credits", "guestPasses"] as const;
  export function format(cost: Cart.Entry.Cost) {
    if (Object.values(cost).every((value) => value === 0)) return "Free";

    return Entry.Cost.units
      .filter((unit) => cost[unit])
      .map((unit) => format[unit](cost[unit]!))
      .join(" + ");
  }
  format.cents = Money.format;
  format.credits = (value: number) =>
    `${value} Credit${value === 1 ? "" : "s"}`;
  format.guestPasses = (value: number) =>
    `${value} Guest Pass${value === 1 ? "" : "es"}`;
  export const render = (cost: Cart.Entry.Cost) =>
    Entry.Cost.units
      .filter((unit) => cost[unit])
      .map((unit, i) => (
        <span class="inline-block">
          {i > 0 && <>&nbsp;+ </>}
          {Cost.format[unit](cost[unit]!)}
        </span>
      ));
}

Entry.Cost = Cost;

export default Cart;

const CartContext = createContext<{ entriesAsData: boolean }>();
